import React, { useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { PusherChannel } from "@activecollab/activecollab-react-pusher";
import { SlideLeftRightTransition } from "@activecollab/components";
import classnames from "classnames";
import moment from "moment";
import PropTypes from "prop-types";
import { makeGetLastMessageByConversationSelector } from "~/store/selectors/selector.conversationMessages.jsx";
// import { useAngularContext } from "../../../../AngularContext.jsx";
import ConversationContentInner from "./ConversationContentInner.js";
import { ParticipantsBody } from "./ConversationParticipants/index.jsx";
import { Header } from "./Header.jsx";
import { useConversationContent } from "./useConversationContent.js";
import { ConversationContentContextProvider } from "../../context/ConversationContentContext.jsx";
import { ConversationProvider } from "../../ConversationProvider.jsx";

export const ConversationContent = ({
  id,
  name,
  members,
  type,
  parentType,
  parentId,
  onKeyDown,
  onClose,
  onBack,
  focused,
  className,
}) => {
  const ref = useRef(null);
  const {
    screen,
    setScreen,
    mode,
    setMode,
    selectedMessageIds,
    newMessagesSince,
    setNewMessagesSince,
    channelName,
    onChangeSelectedMessage,
    mapOfCheckedMessages,
    setIsLeavingConversation,
    manualSetNewMessageSince,
  } = useConversationContent(id);

  const getLastMessageByConversation = useMemo(() => makeGetLastMessageByConversationSelector(id), [id]);
  const lastMessage = useSelector(getLastMessageByConversation);
  const lastMessageCreatedOn = lastMessage?.created_on;
  // const { updateRouteParams } = useAngularContext();

  React.useEffect(() => {
    setNewMessagesSince((prevState) => {
      if (focused) {
        return prevState < lastMessageCreatedOn ? prevState : undefined;
      }
      return typeof prevState === "undefined" ? moment.utc().unix() : prevState;
    });
  }, [focused, lastMessageCreatedOn, setNewMessagesSince]);

  const handleCloseOptions = useCallback(() => {
    ref.current?.focus();
  }, []);

  const handleLeave = useCallback(() => {
    setIsLeavingConversation(true);
    typeof onBack === "function" && onBack();
  }, [onBack, setIsLeavingConversation]);

  return (
    <ConversationContentContextProvider
      value={{
        screen,
        setScreen,
        manualSetNewMessageSince,
        mode,
        setMode,
        mapOfCheckedMessages,
        onChangeSelectedMessage,
        newMessagesSince,
        selectedMessageIds,
        setNewMessagesSince,
      }}
    >
      <ConversationProvider id={id}>
        <PusherChannel name={channelName}>
          <div
            className={classnames("tw-flex tw-flex-col tw-overflow-hidden tw-relative", className)}
            onKeyDown={onKeyDown}
            data-no-focus-lock="true"
            tabIndex={0}
            ref={ref}
          >
            <Header
              name={name}
              type={type}
              members={members}
              parentType={parentType}
              parentId={parentId}
              onLeave={handleLeave}
              onClose={onClose}
              onBack={onBack}
              conversationId={id}
              onShowParticipants={handleCloseOptions}
            />
            <div
              className={classnames(
                "tw-flex",
                "tw-flex-1",
                "tw-grow",
                "tw-flex-col",
                "tw-relative",
                "tw-overflow-y-auto",
                "tw-overflow-x-hidden",
                "tw-w-full"
              )}
            >
              <SlideLeftRightTransition
                in={screen === "participants"}
                direction={screen === "participants" ? "left" : "right"}
              >
                <ParticipantsBody
                  conversationId={id}
                  onLeaveSuccess={handleLeave}
                />
              </SlideLeftRightTransition>

              <SlideLeftRightTransition
                in={screen === "conversation"}
                direction={screen === "participants" ? "left" : "right"}
              >
                <ConversationContentInner
                  id={id}
                  members={members}
                  type={type}
                  onClose={onClose}
                />
              </SlideLeftRightTransition>
            </div>
          </div>
        </PusherChannel>
      </ConversationProvider>
    </ConversationContentContextProvider>
  );
};

ConversationContent.propTypes = {
  id: PropTypes.number.isRequired,
  type: PropTypes.string,
  name: PropTypes.string,
  members: PropTypes.arrayOf(PropTypes.number),
  parentType: PropTypes.string,
  parentId: PropTypes.number,
  onKeyDown: PropTypes.func,
  onClose: PropTypes.func,
  onBack: PropTypes.func,
  focused: PropTypes.bool,
  className: PropTypes.string,
};

ConversationContent.defaultProps = {
  focused: false,
  members: [],
};
